import { forkJoin, Observable } from 'rxjs';import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MasterDataService } from 'src/app/Services/master-data.service';
import { FormsModule } from "@angular/forms";
import { CommonModule } from '@angular/common';
import { ArticleTypeDTO, ArticleTypeImageDTO } from 'src/app/DTOs/ArticleTypeDTO';
import { PointOfMeasurementDTO } from 'src/app/DTOs/PointOfMeasurementsDTO';

import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { DataResult, DataStateChangeEvent, GridDataResult, PageChangeEvent, State, pageSkip, pageTake, process } from 'src/app/Shared/prime-ng/grid-data';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

interface PendingArticleTypeImage {
  file: File;
  previewUrl: string;
}

@Component({
  selector: 'app-article-type-detail',
  standalone: true,
  imports: [PrimeNgModule, FormsModule, CommonModule, RouterModule],
  templateUrl: './article-type-detail.component.html',
  styleUrl: './article-type-detail.component.css'
})
export class ArticleTypeDetailComponent implements OnInit {
  articleType: ArticleTypeDTO | null = {
    databaseId: 0,
    articleTypeId: 0,
    english: '',
    german: '',
    pointOfMeasurements: [],
    imageUrl: '',
    images: [],
    sizes: []
  };

  public pointOfMeasurements: PointOfMeasurementDTO[] = [];
  public currentPointOfMeasurements: PointOfMeasurementDTO[] = [];
  public originalPointOfMeasurements: PointOfMeasurementDTO[] = [];
  public gridView: GridDataResult = { data: [], total: 0 };
  public gridState: State = { skip: 0, take: 10 };
  editMode: boolean = false;
  cancelDialog: boolean = false;
  isUploading = false;
  maxFileSizeBytes = 5 * 1024 * 1024;
  pendingImages: PendingArticleTypeImage[] = [];

  constructor(
    private route: ActivatedRoute,
    private masterData: MasterDataService,
    private router: Router,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.loadArticleTypeDetail();
    this.updateGridView();
  }

  private updateGridView() {
    this.gridView = process(this.pointOfMeasurements, this.gridState);
  }

  public dataStateChange(state: State): void {
    this.gridState = state;
    this.updateGridView();
  }

  public pageChange(event: PageChangeEvent): void {
    this.gridState.skip = pageSkip(event);
    this.gridState.take = pageTake(event);
    this.updateGridView();
  }

  public addHandler() {
    try {
      const newPOM: PointOfMeasurementDTO = {
        pointOfMeasurementId: 0,
        titleEn: '',
        titleDe: '',
        tolerancePlus: 0,
        toleranceMinus: 0,
        controlMeasure: false,
        sorting: 0
      };

      this.pointOfMeasurements = [...this.pointOfMeasurements, newPOM];
      this.updateGridView();
      this.notification('New Measurement Point was added', 'success');
    } catch {
      this.notification('Error while adding Measurement Point', 'error');
    }
  }

  public editHandler() {
    this.editMode = !this.editMode;
    this.currentPointOfMeasurements = this.pointOfMeasurements.map(pom => ({ ...pom }));
  }

  public openCancelDialog() {
    this.cancelDialog = true;
  }

  public cancelHandler() {
    try {
      this.pointOfMeasurements = this.originalPointOfMeasurements.map(pom => ({ ...pom }));
      this.toggleCancelDialog();
      this.updateGridView();
      this.notification('Changes were canceled', 'info');
      this.router.navigate(['/article-types']);
    } catch {
      this.notification('Error while canceling the changes', 'error');
    }
  }

  public saveHandler() {
    this.editMode = !this.editMode;
    this.updateGridView();
  }

  public removeHandler(item: PointOfMeasurementDTO) {
    const index = this.pointOfMeasurements.indexOf(item);
    if (index > -1) {
      this.pointOfMeasurements.splice(index, 1);
      this.updateGridView();
      this.notification(`Measurement Point "${item.titleEn}" was removed`, 'warning');
    }
  }

  saveArticleType() {
    if (!this.articleType) return;

    this.articleType.english = this.articleType.english?.trim() ?? '';
    this.articleType.german = this.articleType.german?.trim() ?? '';
    this.pointOfMeasurements = this.pointOfMeasurements.map(pom => ({
      ...pom,
      titleEn: pom.titleEn?.trim() ?? '',
      titleDe: pom.titleDe?.trim() ?? ''
    }));

    const validationErrors = this.validateArticleType();
    if (validationErrors.length > 0) {
      this.updateGridView();
      this.notification(`Please check:\n\n• ${validationErrors.join('\n• ')}`, 'error');
      return;
    }

    this.articleType.pointOfMeasurements = this.pointOfMeasurements;
    if (this.articleType.databaseId && this.articleType.databaseId > 0) {
      this.masterData.updateArticleType(this.articleType).subscribe({
        next: () => {
          this.notification('Article Type was updated', 'success');
          this.handlePendingImage();
        },
        error: () => this.notification('Error', 'error')
      });
    } else {
      this.masterData.createArticleType(this.articleType).subscribe({
        next: (newId: number) => {
          if (this.articleType) {
            this.articleType.articleTypeId = newId;
            this.articleType.databaseId = newId;
          }
          this.notification('Article Type was created', 'success');
          this.handlePendingImage();
        },
        error: () => this.notification('Error', 'error')
      });
    }
  }

  private validateArticleType(): string[] {
    const errors: string[] = [];

    if (!this.articleType?.english) {
      errors.push('English title is required');
    }

    if (!this.articleType?.german) {
      errors.push('German title is required');
    }

    this.pointOfMeasurements.forEach((pom, index) => {
      if (!pom.titleEn || !pom.titleDe) {
        errors.push(`Point of Measurement ${index + 1}: English and German title are required`);
      }
    });

    return errors;
  }

  private handlePendingImage() {
    if (this.pendingImages.length > 0 && this.articleType) {
      this.uploadImages();
    } else {
      this.router.navigate(['/article-types']);
    }
  }
  private uploadImages() {
    if (!this.articleType) return;
    this.isUploading = true;

    const existingImages = this.articleType.images ?? [];
    const uploads: Observable<ArticleTypeImageDTO>[] = this.pendingImages.map((pendingImage, index) =>
      this.masterData.uploadArticleTypeImage(
        this.articleType!.articleTypeId,
        pendingImage.file,
        existingImages.length === 0 && index === 0
      )
    );

    forkJoin(uploads).subscribe({
      next: (uploadedImages) => {
        if (this.articleType) {
          this.articleType.images = [...(this.articleType.images ?? []), ...uploadedImages];
          this.syncPrimaryImage();
        }
        this.pendingImages = [];
        this.notification('Images uploaded successfully.', 'success');
        this.isUploading = false;
        this.router.navigate(['/article-types']);
      },
      error: (err) => {
        console.error('Upload error:', err);
        this.notification('Error while uploading images.', 'error');
        this.isUploading = false;
      }
    });
  }
  cancelEdit() {
    this.articleType = null;
    this.router.navigate(['/article-types']);
  }

  private loadArticleTypeDetail() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (id) {
      this.masterData.getArticleTypeDetail(id).subscribe({
        next: (data: ArticleTypeDTO) => {
          this.articleType = data;
          this.pendingImages = [];
          if (this.articleType) {
            this.articleType.databaseId = this.articleType.articleTypeId;
            this.articleType.images = this.articleType.images ?? [];
            this.syncPrimaryImage();
          }
          if (this.articleType?.pointOfMeasurements) {
            this.pointOfMeasurements = this.articleType.pointOfMeasurements.map(pom => ({ ...pom }));
            this.originalPointOfMeasurements = this.articleType.pointOfMeasurements.map(pom => ({ ...pom }));
          } else {
            this.pointOfMeasurements = [];
            this.originalPointOfMeasurements = [];
          }
          this.updateGridView();
        },
        error: () => this.notification('Error while loading Article Type', 'error')
      });
    } else {
      this.pointOfMeasurements = [];
      this.originalPointOfMeasurements = [];
      this.updateGridView();
    }
  }

  toggleCancelDialog() {
    this.cancelDialog = !this.cancelDialog;
  }

  notification(message: string, state: any) {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true }
    });
  }


  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length === 0 || !this.articleType) return;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        this.notification('Only image files are allowed.', 'error');
        continue;
      }
      if (file.size > this.maxFileSizeBytes) {
        this.notification('Image is too large (max 5 MB).', 'error');
        continue;
      }
      this.addPendingImage(file);
    }

    input.value = '';
  }

  private addPendingImage(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.pendingImages = [
        ...this.pendingImages,
        {
          file,
          previewUrl: e.target.result
        }
      ];
    };
    reader.readAsDataURL(file);
  }

  removePendingImage(index: number) {
    this.pendingImages = this.pendingImages.filter((_, i) => i !== index);
  }

  deleteImage(image: ArticleTypeImageDTO) {
    if (!this.articleType || this.isUploading) return;
    this.masterData.deleteArticleTypeImage(this.articleType.articleTypeId, image.articleTypeImageId).subscribe({
      next: () => {
        if (!this.articleType) return;
        this.articleType.images = (this.articleType.images ?? [])
          .filter(existingImage => existingImage.articleTypeImageId !== image.articleTypeImageId);

        if (image.isPrimary && this.articleType.images.length > 0) {
          this.articleType.images[0].isPrimary = true;
        }

        this.syncPrimaryImage();
        this.notification('Image was removed', 'success');
      },
      error: () => this.notification('Error while removing image.', 'error')
    });
  }

  setPrimaryImage(image: ArticleTypeImageDTO) {
    if (!this.articleType || image.isPrimary || this.isUploading) return;
    this.masterData.setArticleTypePrimaryImage(this.articleType.articleTypeId, image.articleTypeImageId).subscribe({
      next: () => {
        if (!this.articleType) return;
        this.articleType.images = (this.articleType.images ?? []).map(existingImage => ({
          ...existingImage,
          isPrimary: existingImage.articleTypeImageId === image.articleTypeImageId
        }));
        this.syncPrimaryImage();
        this.notification('Primary image was updated', 'success');
      },
      error: () => this.notification('Error while updating primary image.', 'error')
    });
  }

  clearPendingImages(imageInput: HTMLInputElement) {
    this.pendingImages = [];
    imageInput.value = '';
  }

  private syncPrimaryImage() {
    if (!this.articleType) return;
    const primaryImage = (this.articleType.images ?? []).find(image => image.isPrimary)
      ?? (this.articleType.images ?? [])[0];
    this.articleType.imageUrl = primaryImage?.imageUrl ?? '';
  }
}
